import { BiddingLifecycleAction } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import { useTransitionBiddingIntradayOrderState } from '~/hooks/bidding/useTransitionBiddingIntradayOrderState'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { translateIntradayServerError } from '~/models/intradayServerError'
import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'

/** 行の「下書きに戻す」 ([[useIntradayReturnToDraft]])。 */
export type IntradayReturnToDraft = {
  /** 送信中の札を下書きに戻す。 */
  returnToDraft: (row: IntradayBidRow) => void
  /** 遷移の最中か (連打の抑止に使う)。 */
  isReturning: boolean
}

/**
 * 送信中の札の「下書きに戻す」 (AC-BIN-115)。 入札一覧 (`BidCreateTab`) と板情報の自社入札
 * (`MarketBoardTab`) がそれぞれ持ち、 行の [[IntradayBidRowActions]] に渡す。
 *
 * design の判断で確認 modal は挟まない (送り直しを止めて同じ札を下書きにするだけで、 内容は
 * 失われず下書きから出し直せる)。 成功は「下書きに戻しました」 のトースト、 拒否 (GC・送信の
 * 最中・画面を開いた後の更新・確定済ワークフロー) は理由ごとの文言のトーストを出す。 一覧は
 * mutation hook の invalidate で取り直される。
 */
export const useIntradayReturnToDraft = (): IntradayReturnToDraft => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  // 提出・取消など同じ画面の他の状態遷移とは別インスタンスにして loading 状態を分離する。
  const { mutateAsync, isPending } = useTransitionBiddingIntradayOrderState()

  const returnToDraft = async (row: IntradayBidRow): Promise<void> => {
    if (isPending) return
    try {
      await mutateAsync({
        id: row.id,
        action: BiddingLifecycleAction.RETURN_TO_DRAFT,
        expectedUpdatedAt: row.updatedAt,
      })
    } catch (err) {
      showErrorToast({ title: translateIntradayServerError(err, t) })
      return
    }
    showSuccessToast({ title: t('page:biddingIntraday.returnToDraft.success') })
  }

  return {
    returnToDraft: (row) => {
      void returnToDraft(row)
    },
    isReturning: isPending,
  }
}
